import type {
  FetchResult,
  NextLink,
  Observable,
  Operation,
} from '@apollo/client/core';
import { ApolloLink, fromPromise } from '@apollo/client/core';
import { OperationQueuing } from 'apollo-link-token-refresh';
import axios from 'axios';
import {
  localStorageGetItem,
  localStorageRemoveItem,
  localStorageSetItem,
} from 'src/utils/localStorage';

import { STORAGE_KEYS } from '@constants/app';
import { ROUTES } from '@constants/routes';

import { sessionStorageSetItem } from '@utils/sessionStorage';

const refreshTokenMutation = `
  mutation refreshToken($refreshToken: String!){
  authentication {
    refreshToken(data:{refreshToken: $refreshToken}){
      accessTokenExpTimestamp
    }
  }
}
`;

const isBrowserAvailable = typeof window !== 'undefined';
const prevPath = isBrowserAvailable
  ? window.location.pathname + window.location.search
  : '';
export class TokenRefreshLink extends ApolloLink {
  private readonly queue: OperationQueuing;
  private fetching: boolean;
  private readonly gap = 5 * 1000; // 5s

  constructor() {
    super();
    this.fetching = false;
    this.queue = new OperationQueuing();
  }

  public request(
    operation: Operation,
    forward: NextLink,
  ): Observable<FetchResult> | null {
    if (typeof forward !== 'function') {
      throw new Error(
        '[Token Refresh Link]: Token Refresh Link is a non-terminating link and should not be the last in the composed chain',
      );
    }

    return fromPromise(
      this.isTokenValidOrUndefined().then(tokenValidOrUndefined => {
        if (tokenValidOrUndefined) {
          return forward(operation);
        } else {
          if (!this.fetching) {
            this.fetching = true;
            axios
              .post(
                import.meta.env.APP_API_GRAPHQL_ENDPOINT,
                JSON.stringify({
                  query: refreshTokenMutation,
                  variables: {
                    refreshToken: this.getRefreshToken(),
                  },
                }),
                {
                  withCredentials: true,
                  headers: { 'Content-Type': 'application/json' },
                },
              )
              .then(response => {
                const newExpTime =
                  response.data?.data?.authentication.refreshToken
                    .accessTokenExpTimestamp;
                if (newExpTime == null) {
                  throw Error('Failed to get token from BE');
                }
                localStorageSetItem(
                  STORAGE_KEYS.ACCESS_GRAPHQL_TOKEN_EXPIRE,
                  newExpTime.toString(),
                );
              })
              .catch(error => {
                console.log(error);
                this.resetTokens();
              })
              .finally(() => {
                this.fetching = false;
                this.queue.consumeQueue();
              });
          }

          return this.queue.enqueueRequest({
            operation,
            forward,
          });
        }
      }),
    ).flatMap(val => val);
  }

  /* eslint-disable @typescript-eslint/explicit-function-return-type */
  private readonly isTokenValidOrUndefined = async () => {
    return (
      typeof this.getRefreshToken() !== 'string' || !this.isAccessTokenExpired()
    );
  };

  private readonly getRefreshToken = () => {
    return localStorageGetItem(STORAGE_KEYS.REFRESH_GRAPHQL_TOKEN);
  };

  private readonly isAccessTokenExpired = () => {
    const expiredAt = new Date(
      Number(localStorageGetItem(STORAGE_KEYS.ACCESS_GRAPHQL_TOKEN_EXPIRE)) *
        1000 -
        this.gap,
    );
    return expiredAt < new Date();
  };

  private readonly resetTokens = () => {
    localStorageRemoveItem(STORAGE_KEYS.ACCESS_GRAPHQL_TOKEN_EXPIRE);
    localStorageRemoveItem(STORAGE_KEYS.REFRESH_GRAPHQL_TOKEN);
    sessionStorageSetItem('prevPath', prevPath);
    window.location.replace(ROUTES.LOG_OUT);
  };
}
